import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { track, ANALYTICS_EVENTS } from '../../utils/productAnalytics';
import {
  CUSTOM_IMAGE_SIZE_HINT,
  CUSTOM_IMAGE_SIZE_MAX,
  CUSTOM_IMAGE_SIZE_MIN,
  formatCustomImageSize,
  validateCustomImageSize,
} from '../../utils/customImageSize';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import Sidebar from '../../components/sidebar/Sidebar';
import Loading from '../../components/common/Loading';
import imageGenerationService, { GeneratedImage, ImageHistorySort, TemporaryImage, ImageGenerationJob, SamplePrompt, type StyleRating } from '../../services/api/imageGenerationService';
import {
  GenerationInputMode,
  GenerationInputModeTabs,
  MagicResizeModal,
  ProductGenerationPanel,
  ProductImageSelectionModal,
  ProductInfoCopyModal,
  ProductSelectionData,
  ProductSelectionModal,
  PromptGeneratorModal,
  SelectedProductImage,
  SelectedStyle,
  StyleFeedbackButtons,
  StyleSelectModal,
} from '../../components/image-generation';
import BannerLibraryModal, { SelectedBanner } from '../../components/image-generation/BannerLibraryModal';
import BannerImageEditModal from '../../components/image-generation/BannerImageEditModal';
import ImageVersionNavigator from '../../components/image-generation/ImageVersionNavigator';
import GeneratingJobsSection from '../../components/image-generation/GeneratingJobsSection';
import materialStyleService from '../../services/api/materialStyleService';
import JobDetailModal from '../../components/image-generation/JobDetailModal';
import {
  fetchAutoStyles,
  filterMaterialsByStyle,
  pickRandomDistinctStyles,
  pickRandomStyle,
  RecommendedStyle,
} from '../../services/api/autoStyleService';
import { ImageIcon, Download, Upload, FolderOpen, X, Users, User, Globe, Copy, Settings, Video, Heart, ArrowDownAZ } from 'lucide-react';
import VideoGenerationTab from '../../components/video-generation/VideoGenerationTab';
import { shouldShowPrompt } from '../../utils/environment';
import { buildCompetitorAnalysisContext } from '../../utils/competitorAnalysisContext';
import { useCachedImageUrl } from '../../hooks/useCachedImageUrl';
import { useNavigateToVideo } from '../../hooks/useNavigateToVideo';
import { selectOrganization } from '../../redux/organization/organizationSlice';
import { ResponsiveAppShell, TaskySelect, TaskyTabs, TaskyTopBar, useConfirm, type TaskyTabItem } from '../../components/tasky';
import { useToast } from '../../contexts/ToastContext';
import { buildProductGenerationPrompt } from '../../utils/productGenerationPrompt';
import { useSetAdmanPageContext } from '../../components/adman/useAdmanPageContext';
import { useAdmanRefreshHandler } from '../../components/adman/useAdmanRefreshHandler';
import { IMAGE_GENERATION_MODEL } from '../../constants/imageModel';

// Image card component with loading state to prevent layout shift
interface ImageCardProps {
  image: GeneratedImage;
  onClick: () => void;
  onDownload: (e: React.MouseEvent) => void;
  onToggleFavorite: (e: React.MouseEvent) => void;
  historyView: 'my' | 'organization' | 'favorites';
  isGlobalSampleAdmin: boolean;
  loading?: 'eager' | 'lazy';
  onToggleGlobalSample?: (imageId: number) => void;
  onStyleRated?: (imageId: number, rating: StyleRating | null) => void;
}

const ImageCard = React.memo<ImageCardProps>(({ image, onClick, onDownload, onToggleFavorite, historyView, isGlobalSampleAdmin, loading = 'lazy', onToggleGlobalSample, onStyleRated }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { cachedUrl, onImageError } = useCachedImageUrl(image.id, image.url);

  const handleToggleGlobalSample = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleGlobalSample) {
      onToggleGlobalSample(image.id);
    }
  };

  return (
    <div
      className="bg-white border border-warm-border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group"
      onClick={onClick}
    >
      <div className="relative" style={{ minHeight: isLoaded ? 'auto' : '200px' }}>
        {/* Skeleton loader */}
        {!isLoaded && !hasError && (
          <div className="absolute inset-0 bg-gray-100 animate-pulse flex items-center justify-center">
            <ImageIcon className="w-8 h-8 text-gray-300" />
          </div>
        )}
        {/* Error state */}
        {hasError && (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center" style={{ minHeight: '200px' }}>
            <div className="text-center text-gray-400">
              <ImageIcon className="w-8 h-8 mx-auto mb-2" />
              <span className="text-xs">読み込み失敗</span>
            </div>
          </div>
        )}
        <img
          src={cachedUrl}
          alt={image.prompt}
          className={`w-full h-auto object-cover transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          loading={loading}
          decoding="async"
          onLoad={() => setIsLoaded(true)}
          onError={() => {
            onImageError(); // Fallback to original URL if blob URL is invalid
            setHasError(true);
            setIsLoaded(true);
          }}
          style={{ display: hasError ? 'none' : 'block' }}
        />
        {/* Global Sample Badge */}
        {image.is_global_sample && (
          <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1">
            <Globe className="w-3 h-3" />
            サンプル
          </div>
        )}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex space-x-1">
            <button
              onClick={onToggleFavorite}
              className={`h-8 w-8 p-0 rounded-md shadow-md flex items-center justify-center ${
                image.is_favorited ? 'bg-rose-500 text-white hover:bg-rose-600' : 'bg-white hover:bg-gray-100'
              }`}
              title={image.is_favorited ? 'お気に入りを解除' : 'お気に入りに追加'}
            >
              <Heart className={`w-4 h-4 ${image.is_favorited ? 'fill-current' : ''}`} />
            </button>
            {/* Global Sample Toggle Button (Admin only) */}
            {isGlobalSampleAdmin && (
              <button
                onClick={handleToggleGlobalSample}
                className={`h-8 w-8 p-0 rounded-md shadow-md flex items-center justify-center ${
                  image.is_global_sample
                    ? 'bg-blue-500 text-white hover:bg-primary'
                    : 'bg-white hover:bg-gray-100'
                }`}
                title={image.is_global_sample ? '全組織への表示を解除' : '全組織に表示'}
              >
                <Globe className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={onDownload}
              className="h-8 w-8 p-0 bg-white rounded-md shadow-md flex items-center justify-center hover:bg-gray-100"
            >
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      <div className="p-3">
        {/* Prompt - Shown for freetext mode, or all modes in staging/local environment */}
        {(shouldShowPrompt() || image.input_mode === 'freetext') && (
          <p className="text-sm text-gray-600 mb-2 line-clamp-3">{image.prompt}</p>
        )}
        {image.product_name && (
          <div className="mb-2 text-xs text-gray-600 bg-gray-50 border border-warm-border rounded px-2 py-1 truncate">
            {image.product_name}
          </div>
        )}
        <div className="flex items-center justify-end text-xs text-gray-500">
          <span>{new Date(image.created_at).toLocaleDateString('ja-JP')}</span>
        </div>
        {historyView !== 'my' && image.user && (
          <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
            <User className="w-3 h-3" />
            <span>{image.user.name || image.user.email}</span>
          </div>
        )}
        {image.style_id && (
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="truncate text-xs text-gray-500">{image.style_name ?? '選択スタイル'}</span>
            <StyleFeedbackButtons
              imageId={image.id}
              styleId={image.style_id}
              currentRating={image.style_rating ?? null}
              source="generated_image_card"
              onRated={(rating) => onStyleRated?.(image.id, rating)}
            />
          </div>
        )}
      </div>
    </div>
  );
});

// Helper component for displaying cached images (usable in conditional renders)
const CachedImg = React.memo<{ imageId: number; src: string; alt: string; className?: string; style?: React.CSSProperties }>(({ imageId, src, alt, className, style }) => {
  const { cachedUrl, onImageError } = useCachedImageUrl(imageId, src);
  return <img src={cachedUrl} alt={alt} className={className} style={style} onError={onImageError} />;
});

const formatImageSizeLabel = (size: string): string => {
  const [width, height] = size.split('x').map(Number);
  if (!width || !height) return size;
  return `横${width}×縦${height}px`;
};

const ImageGenerationPage: React.FC = () => {
  const { showToast } = useToast();
  const confirm = useConfirm();
  const { navigateToVideo } = useNavigateToVideo();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useSelector((state: RootState) => state.auth.user);
  const organization = useSelector(selectOrganization);
  const adScoreEnabled = organization?.enable_ad_score ?? false;
  const isGlobalSampleAdmin = user?.email === 'leader@example.com';
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState<'image' | 'video'>('image');
  const [prompt, setPrompt] = useState('');
  const [imageSize, setImageSize] = useState('1080x1080');
  // isGenerating state removed - using job-based generation instead
  const [generatedImage, setGeneratedImage] = useState<GeneratedImage | null>(null);
  const [imageHistory, setImageHistory] = useState<GeneratedImage[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [uploadedImageFile, setUploadedImageFile] = useState<File | null>(null);
  const [uploadedImagePreview, setUploadedImagePreview] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<GeneratedImage | null>(null);
  const [jobDetailJobId, setJobDetailJobId] = useState<string | null>(null);

  // Auto-open JobDetailModal when ?job=<uuid> query param is present
  // (used when navigating from /home 最近の作業 → このプロジェクトを開く)
  useEffect(() => {
    const jobId = searchParams.get('job');
    if (jobId) {
      setJobDetailJobId(jobId);
      // Clean the URL so reload doesn't keep re-opening the modal
      const next = new URLSearchParams(searchParams);
      next.delete('job');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [displayedContent, setDisplayedContent] = useState<GeneratedImage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showImageSelectionModal, setShowImageSelectionModal] = useState(false);
  const [showBannerLibraryModal, setShowBannerLibraryModal] = useState(false);
  const [showProductInfoModal, setShowProductInfoModal] = useState(false);
  const [showProductSelectionModal, setShowProductSelectionModal] = useState(false);
  const [showPromptGeneratorModal, setShowPromptGeneratorModal] = useState(false);
  const [showMagicResizeModal, setShowMagicResizeModal] = useState(false);
  const [showStyleSelectModal, setShowStyleSelectModal] = useState(false);
  const [showImageEditModal, setShowImageEditModal] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState<SelectedStyle | null>(null);
  const [selectedStyles, setSelectedStyles] = useState<SelectedStyle[]>([]);
  const [autoStyleEnabled, setAutoStyleEnabled] = useState(false);
  const [referenceImages, setReferenceImages] = useState<SelectedProductImage[]>([]);
  const [textlessMode, setTextlessMode] = useState(true);
  const [proMode, setProMode] = useState(false);

  // Sample prompts for free text mode
  const [materialPrompts, setMaterialPrompts] = useState<SamplePrompt[]>([]);
  const [bannerPrompts, setBannerPrompts] = useState<SamplePrompt[]>([]);

  // Input mode: product selection vs free text
  const [inputMode, setInputMode] = useState<GenerationInputMode>('product');
  const [selectedProductData, setSelectedProductData] = useState<ProductSelectionData | null>(null);
  // 広告自動化マンに「今この商品を見ている」と伝える（F20-a）。既存ロジックには触れない。
  // 動画タブがアクティブなときは VideoGenerationTab 側が自分の商品選択を伝えるため、
  // ここでは渡さない（渡すと親子の effect 実行順でこちらが後勝ちして上書きしてしまう）。
  useSetAdmanPageContext(
    activeMainTab === 'image' && selectedProductData ? { product_ids: [selectedProductData.product.id] } : null
  );
  const [additionalInstructions, setAdditionalInstructions] = useState('');

  // Custom image size state
  const [useCustomSize, setUseCustomSize] = useState(false);
  const [customWidth, setCustomWidth] = useState('1080');
  const [customHeight, setCustomHeight] = useState('1080');

  // Uploaded reference images state
  const [uploadedReferenceImages, setUploadedReferenceImages] = useState<Array<{
    id: string;
    file?: File;
    preview: string;
    base64: string;
    s3Key?: string;
    category: 'brand_logo' | 'product_image' | 'material';
  }>>([]);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const promptSectionRef = useRef<HTMLDivElement>(null);

  // Function to use generated image: switch to free text mode and add as reference
  const handleUseImageAsReference = async (image: GeneratedImage) => {
    try {
      // Use backend API to get base64 (avoids CORS issues)
      const response = await imageGenerationService.getImageBase64(image.id);

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to get image data');
      }

      // Add to uploaded reference images
      setUploadedReferenceImages(prev => [...prev, {
        id: `generated-${image.id}-${Date.now()}`,
        preview: response.data.url,
        base64: response.data.base64,
        // 元のキーを送ると、どの生成画像を元に作り直したかをジョブから辿れる
        s3Key: image.s3_key || undefined,
        category: 'material',
      }]);

      // Close modal
      setSelectedImage(null);

      // Scroll to prompt section after a short delay for modal to close
      setTimeout(() => {
        promptSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      console.error('Error converting image to reference:', err);
      showToast('画像を参照画像として追加できませんでした', 'error');
    }
  };

  // Multiple image generation state
  const [temporaryImages, setTemporaryImages] = useState<TemporaryImage[]>([]);
  const [selectedTempImage, setSelectedTempImage] = useState<TemporaryImage | null>(null);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [generationProgress, setGenerationProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // Async generation state
  const [imageCount, setImageCount] = useState(3);
  const [useAsyncGeneration, setUseAsyncGeneration] = useState(true);
  const [jobRefreshTrigger, setJobRefreshTrigger] = useState(0);

  // History view state (my images vs organization images)
  const [historyView, setHistoryView] = useState<'my' | 'organization' | 'favorites'>('my');
  const [historySort, setHistorySort] = useState<ImageHistorySort>('newest');
  const [organizationImages, setOrganizationImages] = useState<GeneratedImage[]>([]);
  const [orgCurrentPage, setOrgCurrentPage] = useState(1);
  const [orgTotalPages, setOrgTotalPages] = useState(1);
  const [orgHasMore, setOrgHasMore] = useState(true);
  const [isOrgLoading, setIsOrgLoading] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (imageCount < 2 && selectedStyles.length > 0) {
      setSelectedStyle(selectedStyles[0]);
      setSelectedStyles([]);
    } else if (imageCount >= 2 && selectedStyles.length > imageCount) {
      setSelectedStyles(prev => prev.slice(0, imageCount));
    }
  }, [imageCount, selectedStyles]);

  const categoryTabs: TaskyTabItem<string>[] = [
    { value: 'all', label: 'すべて' },
    { value: 'advertising', label: '広告' },
    { value: 'illustration', label: 'イラスト' },
    { value: 'realistic', label: '実写' },
    { value: 'landscape', label: '風景' },
  ];

  const mainTabs: TaskyTabItem<typeof activeMainTab>[] = [
    { value: 'image', label: '画像生成', icon: <ImageIcon className="w-4 h-4" /> },
    { value: 'video', label: '動画生成', icon: <Video className="w-4 h-4" /> },
  ];

  useEffect(() => {
    if (historyView === 'my') {
      fetchImageHistory();
    }
  }, [currentPage, historyView, historySort]);

  useEffect(() => {
    if (historyView === 'organization' || historyView === 'favorites') {
      fetchOrganizationImageHistory();
    }
  }, [orgCurrentPage, historyView, historySort]);

  // Default textlessMode to true for freetext mode
  useEffect(() => {
    if (inputMode === 'freetext') {
      setTextlessMode(true);
    }
  }, [inputMode]);

  // Fetch both material and banner sample prompts
  useEffect(() => {
    const fetchSamplePrompts = async () => {
      try {
        const [materialData, bannerData] = await Promise.all([
          imageGenerationService.getSamplePrompts('material'),
          imageGenerationService.getSamplePrompts('banner'),
        ]);
        setMaterialPrompts(materialData);
        setBannerPrompts(bannerData);
      } catch (err) {
        console.error('Failed to fetch sample prompts:', err);
      }
    };
    fetchSamplePrompts();
  }, []);

  const fetchImageHistory = async () => {
    try {
      setIsLoading(true);
      const response = await imageGenerationService.getImageHistory(currentPage, 'image_generation', { sort: historySort });
      if (response.success) {
        const newImages = response.data.data;
        if (currentPage === 1) {
          setDisplayedContent(newImages);
        } else {
          // Deduplicate: only add images that don't already exist
          setDisplayedContent(prev => {
            const existingIds = new Set(prev.map(img => img.id));
            const uniqueNewImages = newImages.filter(img => !existingIds.has(img.id));
            return [...prev, ...uniqueNewImages];
          });
        }
        setImageHistory(newImages);
        setTotalPages(response.data.last_page);
        setHasMore(currentPage < response.data.last_page);
      }
    } catch (err: any) {
      console.error('Failed to fetch image history:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // 広告自動化マンの「更新する」ボタンから呼べるようにする（F23-c）。
  // 動画タブがアクティブなときは画像一覧の更新は意味を持たないため登録しない
  // （動画生成タブは複数箇所で使い回されており、干渉を避けるためF23-cの対象外とした）。
  useAdmanRefreshHandler(activeMainTab === 'image' ? fetchImageHistory : null);

  const fetchOrganizationImageHistory = async () => {
    try {
      setIsOrgLoading(true);
      const response = await imageGenerationService.getOrganizationImageHistory(orgCurrentPage, 'image_generation', {
        favorite: historyView === 'favorites',
        sort: historySort,
      });
      if (response.success) {
        const newImages = response.data.data;
        if (orgCurrentPage === 1) {
          setOrganizationImages(newImages);
        } else {
          // Deduplicate: only add images that don't already exist
          setOrganizationImages(prev => {
            const existingIds = new Set(prev.map(img => img.id));
            const uniqueNewImages = newImages.filter(img => !existingIds.has(img.id));
            return [...prev, ...uniqueNewImages];
          });
        }
        setOrgTotalPages(response.data.last_page);
        setOrgHasMore(orgCurrentPage < response.data.last_page);
      }
    } catch (err: any) {
      console.error('Failed to fetch organization image history:', err);
    } finally {
      setIsOrgLoading(false);
    }
  };

  const handleGenerateMultipleImages = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setTemporaryImages([]);
    setSelectedTempImage(null);
    setGenerationProgress(0);

    if (inputMode === 'product' && !selectedProductData) {
      setError('商品を選択してください');
      return;
    }

    if (customSizeError) {
      setError(customSizeError);
      return;
    }

    const generationPrompt = inputMode === 'product' && selectedProductData
      ? `選択した商品を訴求する広告クリエイティブを生成してください。\n${buildProductGenerationPrompt(selectedProductData, additionalInstructions)}`
      : prompt.trim();

    if (!generationPrompt && !selectedStyle && selectedStyles.length === 0 && !autoStyleEnabled) {
      setError('プロンプトを入力してください');
      return;
    }

    try {
      // Build prompt with size and style info
      const effectiveSize = getEffectiveImageSize();
      const sizeLabel = useCustomSize
        ? `カスタム (${formatImageSizeLabel(effectiveSize)})`
        : imageSize === '1080x1080' ? '1:1（横1080×縦1080px）' :
          imageSize === '1080x1350' ? '4:5（横1080×縦1350px）' :
          imageSize === '1080x1920' ? '9:16（横1080×縦1920px）' :
          imageSize === '1920x1080' ? '16:9（横1920×縦1080px）' : '1:1（横1080×縦1080px）';

      // Calculate aspect ratio for API
      const [width, height] = effectiveSize.split('x').map(Number);
      let aspectRatioApi: string | undefined;
      if (width === height) {
        aspectRatioApi = '1:1';
      } else if (width > height) {
        aspectRatioApi = width / height >= 1.7 ? '16:9' : '4:3';
      } else {
        aspectRatioApi = height / width >= 1.7 ? '9:16' : '3:4';
      }

      // Prepare reference images for async generation
      const allRefImages: Array<{
        type: 'brand_logo' | 'product_image' | 'material';
        s3_key?: string;
        image_data?: string;
      }> = [];

      // Add product library images (using s3_key to avoid CORS issues)
      referenceImages.forEach((img) => {
        allRefImages.push({
          type: img.category,
          s3_key: img.s3Key,
        });
      });

      // Add uploaded images (using s3_key for banners, base64 for direct uploads)
      uploadedReferenceImages.forEach((img) => {
        if (img.s3Key) {
          allRefImages.push({
            type: img.category,
            s3_key: img.s3Key,
          });
        } else {
          allRefImages.push({
            type: img.category,
            image_data: img.base64,
          });
        }
      });

      // Free text mode: Use existing prompt with variation hints
      let promptPrefix = `画像サイズ: ${sizeLabel}\n\n`;
      // Add textless mode instruction if enabled
      if (textlessMode) {
        promptPrefix += `【重要】画像内にテキストや文字を含めないでください。文字を一切生成しないでください。\n\n`;
      }
      const competitorAnalysisContext = buildCompetitorAnalysisContext(referenceImages);
      if (competitorAnalysisContext) {
        promptPrefix += `【競合参考情報】\n${competitorAnalysisContext}\n\n`;
      }

      // Variation hints (for 3+ images)
      const variationHints = [
        '', // First batch as-is
        '\n\n【構図バリエーション】異なるカメラアングルや視点で表現してください。',
        '\n\n【構図バリエーション】より寄りのショットや異なる配置で表現してください。',
        '\n\n【構図バリエーション】引きのショットや全体が見える構図で表現してください。',
      ];

      const appealVariationHints = [
        '\n\n【訴求バリエーション】ベネフィットを端的に伝える訴求にしてください。',
        '\n\n【訴求バリエーション】課題解決・悩み解消を中心にした訴求にしてください。',
        '\n\n【訴求バリエーション】信頼感・実績・安心感を中心にした訴求にしてください。',
        '\n\n【訴求バリエーション】限定感・今すぐ試したくなる理由を中心にした訴求にしてください。',
        '\n\n【訴求バリエーション】価格以上の価値やコストパフォーマンスを中心にした訴求にしてください。',
        '\n\n【訴求バリエーション】使いやすさ・手軽さを中心にした訴求にしてください。',
        '\n\n【訴求バリエーション】利用後の理想状態や変化を中心にした訴求にしてください。',
        '\n\n【訴求バリエーション】比較優位・選ばれる理由を中心にした訴求にしてください。',
        '\n\n【訴求バリエーション】新しさ・話題性を中心にした訴求にしてください。',
        '\n\n【訴求バリエーション】具体的な利用シーンを中心にした訴求にしてください。',
        '\n\n【訴求バリエーション】感情に響く共感コピーを中心にした訴求にしてください。',
        '\n\n【訴求バリエーション】専門性・品質の高さを中心にした訴求にしてください。',
        '\n\n【訴求バリエーション】時短・効率化を中心にした訴求にしてください。',
        '\n\n【訴求バリエーション】初めての人にも伝わるわかりやすさを中心にした訴求にしてください。',
        '\n\n【訴求バリエーション】リピートしたくなる魅力を中心にした訴求にしてください。',
        '\n\n【訴求バリエーション】季節性・タイミングのよさを中心にした訴求にしてください。',
        '\n\n【訴求バリエーション】ターゲットの自分ごと化を促す訴求にしてください。',
        '\n\n【訴求バリエーション】購入・問い合わせへの後押しを中心にした訴求にしてください。',
        '\n\n【訴求バリエーション】ブランドらしさや世界観を中心にした訴求にしてください。',
        '\n\n【訴求バリエーション】シンプルで記憶に残る一言訴求にしてください。',
      ];

      // refsForStyle: when a style is provided, trim the user-selected
      // material refs down to max_material_count head (non-material refs
      // pass through unchanged).
      const refsForStyle = (
        style?: SelectedStyle | RecommendedStyle | null,
      ) => {
        const max = style && 'maxMaterialCount' in style
          ? style.maxMaterialCount ?? null
          : style && 'max_material_count' in style
          ? style.max_material_count ?? null
          : null;
        return filterMaterialsByStyle(allRefImages, { max_material_count: max });
      };

      const dispatchGeneration = async (
        basePrompt: string,
        count: number,
        label?: string,
        styleForRefs?: SelectedStyle | RecommendedStyle | null,
      ) => {
        const batchSize = 2;
        const numBatches = Math.ceil(count / batchSize);
        let allSuccess = true;
        const errors: string[] = [];
        const refs = refsForStyle(styleForRefs);

        for (let i = 0; i < numBatches; i++) {
          const imagesInBatch = Math.min(batchSize, count - i * batchSize);
          const variationHint = count >= 3 ? variationHints[Math.min(i, variationHints.length - 1)] : '';
          const batchPrompt = basePrompt + variationHint;

          const [batchEffectiveWidth, batchEffectiveHeight] = getEffectiveImageSize().split('x').map(Number);
          const response = await imageGenerationService.generateAsync(
            batchPrompt,
            imagesInBatch,
            refs.length > 0 ? refs : undefined,
            batchEffectiveWidth,
            batchEffectiveHeight,
            'freetext',
            undefined,
            'image_generation',
            IMAGE_GENERATION_MODEL,
            proMode
          );

          if (!response.success) {
            allSuccess = false;
            errors.push(`${label ? `${label} ` : ''}バッチ${i + 1}: ${response.message}`);
          }
        }

        return { allSuccess, errors };
      };

      const dispatchDistinctPrompts = async (
        prompts: Array<{
          prompt: string;
          label?: string;
          style?: SelectedStyle | RecommendedStyle | null;
        }>,
      ) => {
        let allSuccess = true;
        const errors: string[] = [];
        const [batchEffectiveWidth, batchEffectiveHeight] = getEffectiveImageSize().split('x').map(Number);

        for (let i = 0; i < prompts.length; i++) {
          const item = prompts[i];
          const refs = refsForStyle(item.style ?? null);
          const response = await imageGenerationService.generateAsync(
            item.prompt,
            1,
            refs.length > 0 ? refs : undefined,
            batchEffectiveWidth,
            batchEffectiveHeight,
            'freetext',
            undefined,
            'image_generation',
            IMAGE_GENERATION_MODEL,
            proMode
          );

          if (!response.success) {
            allSuccess = false;
            errors.push(`${item.label ? `${item.label} ` : ''}${i + 1}枚目: ${response.message}`);
          }
        }

        return { allSuccess, errors };
      };

      const buildPromptWithStyle = (style?: SelectedStyle | RecommendedStyle | null) => {
        let builtPrompt = promptPrefix;
        if (style && Object.prototype.hasOwnProperty.call(style, 'stylePrompt')) {
          const selected = style as SelectedStyle;
          builtPrompt += `スタイル: ${selected.name}\n${selected.stylePrompt}\n\n`;
        } else if (style) {
          const recommended = style as RecommendedStyle;
          builtPrompt += `スタイル: ${recommended.name}\n${recommended.prompt_template ?? ''}\n\n`;
        }
        return builtPrompt + generationPrompt;
      };

      const multiStyles = imageCount >= 2 ? selectedStyles.slice(0, imageCount) : [];
      let allSuccess = true;
      const errors: string[] = [];

      if (multiStyles.length > 0) {
        const baseCount = Math.floor(imageCount / multiStyles.length);
        const remainder = imageCount % multiStyles.length;
        setCurrentPrompt(multiStyles.map(style => buildPromptWithStyle(style)).join('\n\n---\n\n'));

        for (let i = 0; i < multiStyles.length; i++) {
          const countForStyle = baseCount + (i < remainder ? 1 : 0);
          if (countForStyle <= 0) continue;
          const result = await dispatchGeneration(
            buildPromptWithStyle(multiStyles[i]),
            countForStyle,
            multiStyles[i].name,
            multiStyles[i],
          );
          if (!result.allSuccess) {
            allSuccess = false;
            errors.push(...result.errors);
          }
        }
      } else {
        // Add style information if selected, or auto-pick from recommendations
        let autoPickedStyle: RecommendedStyle | null = null;
        if (!selectedStyle && autoStyleEnabled && imageCount >= 2) {
          try {
            const autoResp = await fetchAutoStyles({ inputText: generationPrompt, limit: imageCount });
            const autoPickedStyles = pickRandomDistinctStyles(autoResp?.recommended ?? [], imageCount);

            if (autoPickedStyles.length >= imageCount) {
              const prompts = autoPickedStyles.map((style, index) => ({
                prompt: buildPromptWithStyle(style) + appealVariationHints[index % appealVariationHints.length],
                label: style.name,
                style,
              }));
              setCurrentPrompt(prompts.map((item) => item.prompt).join('\n\n---\n\n'));
              const result = await dispatchDistinctPrompts(prompts);
              allSuccess = result.allSuccess;
              errors.push(...result.errors);
              if (allSuccess) {
                setPrompt('');
                setReferenceImages([]);
                uploadedReferenceImages.forEach(img => URL.revokeObjectURL(img.preview));
                setUploadedReferenceImages([]);
                setCurrentPrompt('');
                setTextlessMode(false);
                setJobRefreshTrigger(prev => prev + 1);
              } else if (errors.length > 0) {
                setError(`一部のジョブでエラーが発生しました: ${errors.join(', ')}`);
                setJobRefreshTrigger(prev => prev + 1);
              }
              return;
            }
          } catch (e) {
            console.warn('Auto style selection failed, proceeding without distinct styles:', e);
          }
        }

        if (!selectedStyle && autoStyleEnabled) {
          try {
            const autoResp = await fetchAutoStyles({ inputText: generationPrompt, limit: 20 });
            autoPickedStyle = pickRandomStyle(autoResp?.recommended ?? []);
          } catch (e) {
            console.warn('Auto style selection failed, proceeding without style:', e);
          }
        }

        const basePrompt = buildPromptWithStyle(selectedStyle || autoPickedStyle);
        setCurrentPrompt(basePrompt);
        const result = await dispatchGeneration(
          basePrompt,
          imageCount,
          undefined,
          selectedStyle ?? autoPickedStyle ?? null,
        );
        allSuccess = result.allSuccess;
        errors.push(...result.errors);
      }

      if (allSuccess) {
          setPrompt('');
          setReferenceImages([]);
          uploadedReferenceImages.forEach(img => URL.revokeObjectURL(img.preview));
          setUploadedReferenceImages([]);
          setCurrentPrompt('');
          setTextlessMode(false);
          setJobRefreshTrigger(prev => prev + 1);
      } else if (errors.length > 0) {
        setError(`一部のジョブでエラーが発生しました: ${errors.join(', ')}`);
        setJobRefreshTrigger(prev => prev + 1);
      }
    } catch (err: any) {
      console.error('Error starting image generation:', err);
      setError(err.response?.data?.message || '画像生成ジョブの開始中にエラーが発生しました');
    }
  };

  const handleJobCompleted = async () => {
    // Smart polling: Only add new images if they don't already exist
    try {
      const response = await imageGenerationService.getImageHistory(1, 'image_generation', { sort: historySort });
      if (response.success) {
        const fetchedImages = response.data.data;

        // Use functional update to ensure we always have the latest state
        // This prevents stale closure issues that can cause duplicates
        setDisplayedContent(prev => {
          // Create a Set of existing IDs for O(1) lookup
          const existingIds = new Set(prev.map(img => img.id));

          // Filter to get only actually new images
          const newImages = fetchedImages.filter(img => !existingIds.has(img.id));

          if (newImages.length > 0) {
            // Prepend new images to the beginning of the list
            return [...newImages, ...prev];
          }
          // If no new images, return previous state unchanged
          return prev;
        });

        // Update total pages info
        setTotalPages(response.data.last_page);
        setHasMore(1 < response.data.last_page);
      }
    } catch (err) {
      console.error('Failed to check for new images:', err);
      // On error, fall back to full refresh
      setCurrentPage(1);
      setDisplayedContent([]);
      fetchImageHistory();
    }
  };

  // Handle regenerating a failed job with the same prompt and settings
  const handleRegenerateJob = async (job: ImageGenerationJob) => {
    try {
      setError(null);

      // productモードのジョブは一覧APIに product_id が含まれないため、
      // サーバー側で生成済みのプロンプトを使い freetext として再生成する。
      // プロンプト生成前に失敗したジョブはプロンプトが空なので再生成不可。
      if (job.input_mode === 'product' && !job.prompt) {
        setError('プロンプト生成前に失敗したジョブは再生成できません。商品を選択して再度生成してください。');
        return;
      }

      // Fetch full job detail to get reference_images (not included in list API)
      let referenceImages: Array<{ type: 'brand_logo' | 'product_image' | 'material'; s3_key?: string; image_data?: string }> | undefined;
      if (job.has_reference_images) {
        const detail = await imageGenerationService.getJobDetail(job.id);
        const jobRefImages = detail.job?.reference_images;
        referenceImages = jobRefImages?.map((img: any) => ({
          type: (img.category === 'brand_logo' ? 'brand_logo' :
                 img.category === 'product_image' ? 'product_image' :
                 'material') as 'brand_logo' | 'product_image' | 'material',
          s3_key: img.s3_key,
          image_data: img.image_data,
        }));
      }

      // Start a new async generation job with the same settings
      const response = await imageGenerationService.generateAsync(
        job.prompt,
        job.count,
        referenceImages, // Restore reference images if available
        job.target_width, // Restore custom width
        job.target_height, // Restore custom height
        'freetext', // product モードも生成済みプロンプトを使うため freetext で再生成
        undefined,
        'image_generation',
        IMAGE_GENERATION_MODEL, // 画像生成は gpt-image-2 固定（過去ジョブの model は引き継がない）
        proMode
      );

      if (!response.success) {
        setError(response.message || '再生成に失敗しました');
      }
      // Job will appear in the GeneratingJobsSection automatically via polling
    } catch (err: any) {
      console.error('Error regenerating job:', err);
      setError(err.response?.data?.message || '再生成中にエラーが発生しました');
    }
  };

  const handleSelectAndSave = async () => {
    if (!selectedTempImage || !currentPrompt) return;

    setIsSaving(true);
    setError(null);

    try {
      // Save selected image to database
      // Use s3_url (base URL) instead of url (presigned URL) to avoid column length issues
      const response = await imageGenerationService.saveSelectedImage(
        currentPrompt,
        selectedTempImage.s3_key,
        selectedTempImage.s3_url,
        selectedTempImage.file_size,
        selectedTempImage.ad_score
      );

      if (response.success) {
        // Delete unselected temporary images
        for (const img of temporaryImages) {
          if (img.temp_id !== selectedTempImage.temp_id) {
            try {
              await imageGenerationService.deleteTemporaryImage(img.s3_key);
            } catch (err) {
              console.error('Failed to delete unselected temp image:', err);
            }
          }
        }

        // Reset state
        setGeneratedImage(response.data);
        setTemporaryImages([]);
        setSelectedTempImage(null);
        setCurrentPrompt('');
        setPrompt('');
        setReferenceImages([]);

        // Refresh history
        setCurrentPage(1);
        setDisplayedContent([]);
        fetchImageHistory();
      } else {
        setError(response.message || '画像の保存に失敗しました');
      }
    } catch (err: any) {
      console.error('Error saving image:', err);
      setError('画像の保存中にエラーが発生しました');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelSelection = async () => {
    // Delete all temporary images
    for (const img of temporaryImages) {
      try {
        await imageGenerationService.deleteTemporaryImage(img.s3_key);
      } catch (err) {
        console.error('Failed to delete temp image:', err);
      }
    }

    setTemporaryImages([]);
    setSelectedTempImage(null);
    setCurrentPrompt('');
  };

  const handleDeleteImage = async (id: number) => {
    if (!(await confirm({ title: 'この画像を削除しますか？', tone: 'destructive', confirmText: '削除する' }))) {
      return;
    }

    try {
      const response = await imageGenerationService.deleteImage(id);
      if (response.success) {
        setDisplayedContent(prev => prev.filter(img => img.id !== id));
        if (generatedImage?.id === id) {
          setGeneratedImage(null);
        }
        if (selectedImage?.id === id) {
          setSelectedImage(null);
        }
      }
    } catch (err: any) {
      console.error('Error deleting image:', err);
      showToast('画像の削除に失敗しました', 'error');
    }
  };

  const handleToggleGlobalSample = async (imageId: number) => {
    try {
      const response = await imageGenerationService.toggleGlobalSample(imageId);
      if (response.success && response.data) {
        // Update the image in displayedContent
        setDisplayedContent(prev =>
          prev.map(img =>
            img.id === imageId
              ? { ...img, is_global_sample: response.data!.is_global_sample }
              : img
          )
        );
        // Update the image in organizationImages
        setOrganizationImages(prev =>
          prev.map(img =>
            img.id === imageId
              ? { ...img, is_global_sample: response.data!.is_global_sample }
              : img
          )
        );
        // Update selectedImage if it's the same
        if (selectedImage?.id === imageId) {
          setSelectedImage(prev =>
            prev ? { ...prev, is_global_sample: response.data!.is_global_sample } : null
          );
        }
      }
    } catch (err: any) {
      console.error('Error toggling global sample:', err);
      showToast(err.response?.data?.message || 'フラグの更新に失敗しました', 'error');
    }
  };

  const handleDownloadImage = async (id: number, url: string, prompt: string) => {
    try {
      // Get presigned S3 download URL from backend
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/images/${id}/download`;
      const token = localStorage.getItem('tasky_auth_token');

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Download API failed');
      }

      const data = await response.json();

      if (data.download_url) {
        // Backend returns presigned S3 URL with Content-Disposition: attachment
        const link = document.createElement('a');
        link.href = data.download_url;
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        track(ANALYTICS_EVENTS.DOWNLOAD_COMPLETED, { type: 'image', image_id: id });
      } else {
        throw new Error('No download URL in response');
      }
    } catch (err) {
      console.error('Download failed:', err);
      showToast('画像のダウンロードに失敗しました。しばらくしてからもう一度お試しください。', 'error');
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleMode = () => {
    setEditMode(!editMode);
    setUploadedImageFile(null);
    setUploadedImagePreview(null);
    setPrompt('');
  };

  const handleReferenceImagesSelect = (selected: SelectedProductImage[]) => {
    setReferenceImages(selected);
  };

  // Handle banner selection from Banner Library Modal
  const handleBannerSelect = useCallback((selectedBanners: SelectedBanner[]) => {
    // Use S3 key directly instead of fetching/converting to base64 (avoids CORS issues)
    const bannerImages = selectedBanners.map((banner) => ({
      id: `banner-${banner.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      preview: banner.url,
      base64: '',
      s3Key: banner.s3Key,
      category: 'material' as const,
    }));

    if (bannerImages.length > 0) {
      setUploadedReferenceImages(prev => [...prev, ...bannerImages]);
    }
  }, []);

  const handleRemoveReferenceImage = (imageId: number) => {
    setReferenceImages(prev => prev.filter(img => img.id !== imageId));
  };

  const handleCopyToPrompt = (text: string) => {
    setPrompt(prev => prev ? `${prev}\n\n${text}` : text);
  };

  const handleProductSelected = (data: ProductSelectionData) => {
    setSelectedProductData(data);
    setShowProductSelectionModal(false);
    if (data.textlessMode !== undefined) setTextlessMode(data.textlessMode);
    if (data.selectedImages.length > 0) {
      setReferenceImages(data.selectedImages.map((image) => ({
        id: image.id,
        url: image.url,
        s3Key: image.file_path,
        productId: image.product_id,
        productName: data.product.name,
        fileName: image.file_name,
        category: image.category,
      })));
    }
  };

  // Handle AI-generated prompt confirmation
  const handleAIPromptConfirm = (generatedPrompt: string) => {
    // Replace the prompt completely with AI-generated one
    setPrompt(generatedPrompt);
    setShowPromptGeneratorModal(false);
  };

  // Get effective image size. The entered size is used as-is: out of range
  // values are refused up front (customSizeError) instead of being clamped,
  // which is what silently turned a 2400x1256 request into 2048x1256.
  const getEffectiveImageSize = (): string => (
    useCustomSize ? formatCustomImageSize(customWidth, customHeight) : imageSize
  );

  const customSizeError = useMemo(
    () => (useCustomSize ? validateCustomImageSize(customWidth, customHeight) : null),
    [useCustomSize, customWidth, customHeight],
  );

  const handleReferenceImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newImages: typeof uploadedReferenceImages = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validate file type
      if (!file.type.startsWith('image/')) {
        continue;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        showToast(`${file.name} は10MBを超えているためスキップされました`, 'error');
        continue;
      }

      // Convert to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Remove data URL prefix
          const base64Data = result.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Create preview URL
      const preview = URL.createObjectURL(file);

      newImages.push({
        id: `upload-${Date.now()}-${i}`,
        file,
        preview,
        base64,
        category: 'material', // Default category
      });
    }

    setUploadedReferenceImages(prev => [...prev, ...newImages]);

    // Reset input
    if (uploadInputRef.current) {
      uploadInputRef.current.value = '';
    }
  };

  const handleRemoveUploadedImage = (imageId: string) => {
    setUploadedReferenceImages(prev => {
      const imageToRemove = prev.find(img => img.id === imageId);
      if (imageToRemove) {
        URL.revokeObjectURL(imageToRemove.preview);
      }
      return prev.filter(img => img.id !== imageId);
    });
  };

  const handleUploadedImageCategoryChange = (imageId: string, category: 'brand_logo' | 'product_image' | 'material') => {
    setUploadedReferenceImages(prev =>
      prev.map(img =>
        img.id === imageId ? { ...img, category } : img
      )
    );
  };

  const handleImageClick = (image: GeneratedImage) => {
    setSelectedImage(image);
  };

  const handleToggleImageFavorite = async (image: GeneratedImage) => {
    try {
      const result = await imageGenerationService.toggleImageFavorite(image.id);
      const updateImage = (img: GeneratedImage) =>
        img.id === image.id ? { ...img, is_favorited: result.is_favorited } : img;

      setDisplayedContent(prev => prev.map(updateImage));
      setImageHistory(prev => prev.map(updateImage));
      setOrganizationImages(prev => {
        const updated = prev.map(updateImage);
        return historyView === 'favorites' && !result.is_favorited
          ? updated.filter(img => img.id !== image.id)
          : updated;
      });
      setGeneratedImage(prev => prev?.id === image.id ? { ...prev, is_favorited: result.is_favorited } : prev);
      setSelectedImage(prev => prev?.id === image.id ? { ...prev, is_favorited: result.is_favorited } : prev);
    } catch (err) {
      console.error('Failed to toggle image favorite:', err);
      showToast('お気に入りの更新に失敗しました', 'error');
    }
  };

  const updateImageStyleRating = (imageId: number, rating: StyleRating | null) => {
    const updateImage = (img: GeneratedImage) =>
      img.id === imageId ? { ...img, style_rating: rating } : img;

    setDisplayedContent(prev => prev.map(updateImage));
    setImageHistory(prev => prev.map(updateImage));
    setOrganizationImages(prev => prev.map(updateImage));
    setGeneratedImage(prev => prev?.id === imageId ? { ...prev, style_rating: rating } : prev);
    setSelectedImage(prev => prev?.id === imageId ? { ...prev, style_rating: rating } : prev);
  };

  const handleHistoryViewChange = (nextView: typeof historyView) => {
    setHistoryView(nextView);
    if (nextView === 'my') {
      setCurrentPage(1);
      setDisplayedContent([]);
    } else {
      setOrgCurrentPage(1);
      setOrganizationImages([]);
    }
  };

  const handleHistorySortChange = (sort: ImageHistorySort) => {
    setHistorySort(sort);
    setCurrentPage(1);
    setOrgCurrentPage(1);
    setDisplayedContent([]);
    setOrganizationImages([]);
  };

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container && container.scrollTop + container.clientHeight >= container.scrollHeight - 800) {
      if (historyView === 'my') {
        if (hasMore && !isLoading) {
          setCurrentPage(prev => prev + 1);
        }
      } else {
        if (orgHasMore && !isOrgLoading) {
          setOrgCurrentPage(prev => prev + 1);
        }
      }
    }
  }, [hasMore, isLoading, orgHasMore, isOrgLoading, historyView]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Get the appropriate content based on current view
  const historyViewTabs: TaskyTabItem<typeof historyView>[] = [
    { value: 'my', label: '自分の画像', icon: <User className="w-4 h-4" /> },
    { value: 'organization', label: '組織の画像', icon: <Users className="w-4 h-4" /> },
    {
      value: 'favorites',
      label: 'お気に入り',
      icon: <Heart className={`w-4 h-4 ${historyView === 'favorites' ? 'fill-current' : ''}`} />,
    },
  ];

  const currentContent = historyView === 'my' ? displayedContent : organizationImages;

  const filteredContent = currentContent.filter(image => {
    if (selectedCategory === 'all') return true;
    const promptLower = image.prompt.toLowerCase();
    if (selectedCategory === 'advertising') return promptLower.includes('広告') || promptLower.includes('バナー');
    if (selectedCategory === 'illustration') return promptLower.includes('イラスト');
    if (selectedCategory === 'realistic') return promptLower.includes('実写') || promptLower.includes('写真');
    if (selectedCategory === 'landscape') return promptLower.includes('風景');
    return true;
  });

  const currentLoading = historyView === 'my' ? isLoading : isOrgLoading;
  const currentHasMore = historyView === 'my' ? hasMore : orgHasMore;

  return (
    <ResponsiveAppShell
      sidebar={(
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggleCollapse={setSidebarCollapsed}
        />
      )}
      mobileSidebar={(
        <Sidebar
          collapsed={false}
          onToggleCollapse={() => undefined}
        />
      )}
      topbarTitle="素材生成"
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col bg-white lg:h-screen lg:min-h-0">
        {/* Header */}
        <TaskyTopBar
          title="素材生成"
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        />

        {/* Main Tab Navigation */}
        <div className="shrink-0 overflow-x-auto bg-white px-3 pt-2 sm:px-4">
          <TaskyTabs<typeof activeMainTab>
            aria-label="生成の種類"
            value={activeMainTab}
            onChange={setActiveMainTab}
            items={mainTabs}
            className="w-max sm:w-auto"
          />
        </div>

        {/* Video Tab Content */}
        {activeMainTab === 'video' ? (
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
            <VideoGenerationTab generationSource="image_generation" />
          </div>
        ) : (
        /* Image Tab Content - Main Scrollable Content */
        <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <div className="mx-auto max-w-6xl px-4 py-4 sm:px-6">
            {/* Generation Form */}
              <div ref={promptSectionRef} className="bg-gray-50 rounded-xl p-4 mb-4">
                <div className="space-y-4">
                  {/* PRO Mode（現在は非表示） */}
                  <div>
                    <div className="mt-2 hidden">
                      <button
                        type="button"
                        onClick={() => setProMode(!proMode)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-sm select-none ${
                          proMode
                            ? 'border-amber-500 bg-amber-50 text-amber-700'
                            : 'border-warm-border bg-white text-gray-500 hover:border-warm-border'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={proMode}
                          readOnly
                          className="rounded border-warm-border text-amber-600 focus:ring-amber-500 pointer-events-none"
                        />
                        <span className="font-semibold">PRO</span>
                        <span className="text-xs opacity-70">高品質モデル</span>
                      </button>
                    </div>
                  </div>

                  <GenerationInputModeTabs value={inputMode} onChange={setInputMode} />

                  {/* Prompt Section */}
                  {inputMode === 'product' ? (
                    <ProductGenerationPanel
                      selectedProductData={selectedProductData}
                      additionalInstructions={additionalInstructions}
                      onAdditionalInstructionsChange={setAdditionalInstructions}
                      onSelectProduct={() => setShowProductSelectionModal(true)}
                    />
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          プロンプト
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowProductInfoModal(true)}

                          className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Copy className="w-3 h-3" />
                          商品情報をコピー
                        </button>
                      </div>
                      <textarea
                        data-tutorial="image-generation-prompt"
                        placeholder="例: 現代的なオフィスでチームミーティングをしている様子。明るく開放的な雰囲気で、多様性のあるメンバーが活発に議論している。"
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value.slice(0, 32000))}
                        maxLength={32000}
                        rows={4}
                        className="w-full p-3 border border-warm-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none text-sm"

                      />
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-xs text-gray-500">
                          {prompt.length}/32000
                        </span>
                        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={textlessMode}
                            onChange={(e) => setTextlessMode(e.target.checked)}
                            className="rounded border-warm-border text-primary focus:ring-primary"

                          />
                          テキストなしで生成
                          <span className="text-xs text-gray-400">（画像内に文字を含めない）</span>
                        </label>
                      </div>

                      {/* Sample Prompt Buttons - material only */}
                      {materialPrompts.length > 0 && (
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-500 mb-1.5">
                            サンプルプロンプト（素材用）
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {materialPrompts.map((sp) => (
                              <button
                                key={sp.id}
                                type="button"
                                onClick={() => setPrompt(sp.prompt_text)}
                                className="px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-full hover:bg-purple-100 transition-colors"
                              >
                                {sp.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Style Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      スタイルを選ぶ
                    </label>
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        type="button"
                        data-tutorial="image-generation-style"
                        onClick={() => setShowStyleSelectModal(true)}
                        disabled={autoStyleEnabled}
                        className="flex items-center gap-2 px-4 py-2 border border-warm-border rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Settings className="w-4 h-4" />
                        {imageCount >= 2 ? 'スタイルを複数選ぶ' : 'スタイルを選ぶ'}
                      </button>
                      <label className="flex items-center gap-2 px-3 py-1.5 border border-warm-border rounded-md cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={autoStyleEnabled}
                          onChange={(e) => {
                            setAutoStyleEnabled(e.target.checked);
                            if (e.target.checked) {
                              setSelectedStyle(null);
                              setSelectedStyles([]);
                            }
                          }}
                          className="w-4 h-4 accent-indigo-600"
                        />
                        <span className="text-sm text-gray-700">自動でスタイル適応</span>
                      </label>
                      {imageCount >= 2 && selectedStyles.length > 0 && !autoStyleEnabled && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md max-w-full">
                          <span className="text-sm text-blue-700 font-medium truncate">
                            {selectedStyles.length}件選択中: {selectedStyles.map(style => style.name).join('、')}
                          </span>
                          <button
                            type="button"
                            onClick={() => setSelectedStyles([])}
                            className="text-blue-500 hover:text-blue-700 flex-shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      {imageCount < 2 && selectedStyle && !autoStyleEnabled && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md">
                          <span className="text-sm text-blue-700 font-medium">{selectedStyle.name}</span>
                          <button
                            type="button"
                            onClick={() => setSelectedStyle(null)}
                            className="text-blue-500 hover:text-blue-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Reference Images Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      参考画像（任意）
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setShowImageSelectionModal(true)}

                        className="flex items-center gap-2 px-4 py-2 border border-warm-border rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FolderOpen className="w-4 h-4" />
                        商品画像から選択
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowBannerLibraryModal(true)}
                        className="flex items-center gap-2 px-4 py-2 border border-blue-300 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-blue-700"
                      >
                        <ImageIcon className="w-4 h-4" />
                        自社バナーから選択
                      </button>
                      <button
                        type="button"
                        onClick={() => uploadInputRef.current?.click()}

                        className="flex items-center gap-2 px-4 py-2 border border-warm-border rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Upload className="w-4 h-4" />
                        アップロード
                      </button>
                      <input
                        ref={uploadInputRef}
                        type="file"
                        accept="image/*,.svg,image/svg+xml"
                        multiple
                        onChange={handleReferenceImageUpload}
                        className="hidden"
                      />
                      {(referenceImages.length > 0 || uploadedReferenceImages.length > 0) && (
                        <span className="text-sm text-gray-600">
                          {referenceImages.length + uploadedReferenceImages.length}枚選択中
                        </span>
                      )}
                    </div>

                    {/* Reference Images Preview */}
                    {(referenceImages.length > 0 || uploadedReferenceImages.length > 0) && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {/* Library-selected images */}
                        {referenceImages.map((img) => (
                          <div
                            key={img.id}
                            className="relative group w-20 h-20 rounded-lg overflow-hidden border-2 border-purple-500"
                          >
                            <img
                              src={img.url}
                              alt={img.fileName || '商品画像'}
                              className="w-full h-full object-contain bg-gray-100"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveReferenceImage(img.id)}
                              className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 truncate">
                              {img.category === 'brand_logo' ? 'ロゴ' : img.category === 'product_image' ? '商品' : '素材'}
                            </div>
                          </div>
                        ))}
                        {/* Uploaded images */}
                        {uploadedReferenceImages.map((img) => (
                          <div
                            key={img.id}
                            className="relative group w-20 h-20 rounded-lg overflow-hidden border-2 border-green-500"
                          >
                            <img
                              src={img.preview}
                              alt={img.file?.name || '参照画像'}
                              className="w-full h-full object-contain bg-gray-100"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveUploadedImage(img.id)}
                              className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                            <select
                              value={img.category}
                              onChange={(e) => handleUploadedImageCategoryChange(img.id, e.target.value as 'brand_logo' | 'product_image' | 'material')}
                              onClick={(e) => e.stopPropagation()}
                              className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-0.5 py-0.5 border-none outline-none cursor-pointer"
                            >
                              <option value="brand_logo">ロゴ</option>
                              <option value="product_image">商品</option>
                              <option value="material">素材</option>
                            </select>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">画像サイズ</label>
                      <TaskySelect
                        value={useCustomSize ? 'custom' : imageSize}
                        onChange={(e) => {
                          if (e.target.value === 'custom') {
                            setUseCustomSize(true);
                          } else {
                            setUseCustomSize(false);
                            setImageSize(e.target.value);
                          }
                        }}
                        className="text-sm"
                      >
                        <option value="1080x1080">1:1（横1080×縦1080px）</option>
                        <option value="1080x1350">4:5（横1080×縦1350px）</option>
                        <option value="1080x1920">9:16（横1080×縦1920px）</option>
                        <option value="1920x1080">16:9（横1920×縦1080px）</option>
                        <option value="custom">カスタムサイズ</option>
                      </TaskySelect>
                    </div>

                    {/* Custom Size Inputs */}
                    {useCustomSize && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">カスタムサイズ</label>
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={customWidth}
                            onChange={(e) => setCustomWidth(e.target.value)}
                            min={CUSTOM_IMAGE_SIZE_MIN}
                            max={CUSTOM_IMAGE_SIZE_MAX}
                            step={1}
                            aria-invalid={customSizeError !== null}
                            className={`w-20 p-2 border rounded-md text-sm text-center ${customSizeError ? 'border-red-400' : 'border-warm-border'}`}
                            placeholder="横幅"
                          />
                          <span className="text-gray-500 text-sm">×</span>
                          <input
                            type="number"
                            value={customHeight}
                            onChange={(e) => setCustomHeight(e.target.value)}
                            min={CUSTOM_IMAGE_SIZE_MIN}
                            max={CUSTOM_IMAGE_SIZE_MAX}
                            step={1}
                            aria-invalid={customSizeError !== null}
                            className={`w-20 p-2 border rounded-md text-sm text-center ${customSizeError ? 'border-red-400' : 'border-warm-border'}`}
                            placeholder="縦幅"
                          />
                        </div>
                        {customSizeError ? (
                          <p className="mt-1 text-xs text-red-600">{customSizeError}</p>
                        ) : (
                          <p className="mt-1 text-xs text-gray-500">
                            {CUSTOM_IMAGE_SIZE_HINT}
                          </p>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">生成枚数</label>
                      <TaskySelect
                        value={imageCount}
                        onChange={(e) => {
                          const nextCount = Number(e.target.value);
                          setImageCount(nextCount);
                          if (nextCount >= 2 && selectedStyle) {
                            setSelectedStyles(prev => prev.length > 0 ? prev : [selectedStyle]);
                            setSelectedStyle(null);
                          }
                        }}
                        className="text-sm"
                      >
                        <option value={1}>1枚</option>
                        <option value={2}>2枚</option>
                        <option value={3}>3枚</option>
                        <option value={4}>4枚</option>
                        <option value={5}>5枚</option>
                        <option value={6}>6枚</option>
                        <option value={7}>7枚</option>
                        <option value={8}>8枚</option>
                        <option value={9}>9枚</option>
                        <option value={10}>10枚</option>
                        <option value={12}>12枚</option>
                        <option value={15}>15枚</option>
                        <option value={20}>20枚</option>
                      </TaskySelect>
                    </div>

                    <div className="flex items-end">
                      <button
                        data-tutorial="image-generation-generate"
                        onClick={handleGenerateMultipleImages}
                        disabled={
                          inputMode === 'product'
                            ? !selectedProductData
                            : !prompt.trim() && !selectedStyle && selectedStyles.length === 0 && !autoStyleEnabled
                        }
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-2.5 px-4 rounded-lg disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg font-semibold text-sm"
                      >
                        {`${imageCount}枚生成`}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  )}
                </div>
              </div>

            {/* Generating Jobs Section - Shows between form and history */}
            <GeneratingJobsSection onJobCompleted={handleJobCompleted} onRegenerate={handleRegenerateJob} generationSource="image_generation" refreshTrigger={jobRefreshTrigger} />

            {/* Latest Generated Image */}
            {generatedImage && (
              <div className="bg-white border border-green-200 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-medium text-green-800 mb-3">採用した画像</h3>
                <div className="relative max-w-md">
                  <CachedImg
                    imageId={generatedImage.id}
                    src={generatedImage.url}
                    alt={generatedImage.prompt}
                    className="w-full h-auto rounded-md"
                  />
                </div>
                <div className="flex items-center space-x-2 mt-3">
                  {generatedImage.style_id && (
                    <StyleFeedbackButtons
                      imageId={generatedImage.id}
                      styleId={generatedImage.style_id}
                      currentRating={generatedImage.style_rating ?? null}
                      size="md"
                      source="image_detail_panel"
                      onRated={(rating) => updateImageStyleRating(generatedImage.id, rating)}
                    />
                  )}
                  <button
                    onClick={() => handleDownloadImage(generatedImage.id, generatedImage.url, generatedImage.prompt)}
                    className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white text-sm rounded-md transition-colors"
                  >
                    <Download className="w-4 h-4 inline mr-1" />
                    ダウンロード
                  </button>
                </div>
              </div>
            )}

            {/* History View Toggle (My Images vs Organization Images) */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <TaskyTabs<typeof historyView>
                variant="pill"
                aria-label="表示する画像の範囲"
                value={historyView}
                onChange={handleHistoryViewChange}
                items={historyViewTabs}
              />
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <ArrowDownAZ className="w-4 h-4" />
                <div className="w-40">
                  <TaskySelect
                    value={historySort}
                    onChange={(e) => handleHistorySortChange(e.target.value as ImageHistorySort)}
                    className="h-9 text-sm"
                  >
                    <option value="newest">新しい順</option>
                    <option value="product_name_asc">商品名 A-Z</option>
                    <option value="product_name_desc">商品名 Z-A</option>
                    <option value="product_only">商品情報ありのみ</option>
                  </TaskySelect>
                </div>
              </label>
            </div>

            {/* Category Tabs */}
            <TaskyTabs
              size="md"
              aria-label="画像のカテゴリ"
              value={selectedCategory}
              onChange={setSelectedCategory}
              items={categoryTabs}
              className="mb-6"
            />

            {/* Masonry Grid */}
            {filteredContent.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                {historyView === 'favorites' ? 'お気に入りはまだありません' : 'まだ画像が生成されていません'}
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-start" data-tutorial="image-generation-results">
                {/* CSS columns だと縦流し（列ごとに上→下）になり新着順が読み順にならないため、
                    行方向（左上→右下）に流れる grid を使う */}
                {filteredContent.map((image, index) => (
                  <ImageCard
                    key={image.id}
                    image={image}
                    onClick={() => handleImageClick(image)}
                    onDownload={(e) => {
                      e.stopPropagation();
                      handleDownloadImage(image.id, image.url, image.prompt);
                    }}
                    onToggleFavorite={(e) => {
                      e.stopPropagation();
                      handleToggleImageFavorite(image);
                    }}
                    historyView={historyView}
                    isGlobalSampleAdmin={isGlobalSampleAdmin}
                    loading={index < 8 ? 'eager' : 'lazy'}
                    onToggleGlobalSample={handleToggleGlobalSample}
                    onStyleRated={updateImageStyleRating}
                  />
                ))}
              </div>
            )}

            {/* Loading Indicator */}
            {currentLoading && <Loading text="読み込み中..." />}
            {currentLoading && currentContent.length > 0 && (
              <div className="sticky bottom-4 z-20 flex justify-center pointer-events-none">
                <div className="flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 shadow-lg border border-warm-border text-sm text-gray-700">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  追加読み込み中
                </div>
              </div>
            )}

            {/* End of Content Indicator */}
            {!currentHasMore && filteredContent.length > 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">すべてのコンテンツを表示しました</p>
              </div>
            )}
          </div>
        </div>
        )}

      </div>

      {/* Image Detail Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="relative flex max-h-[calc(100dvh-32px)] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white lg:max-h-[90vh] lg:flex-row">
            <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-black">
              <button
                onClick={() => handleToggleImageFavorite(selectedImage)}
                className={`absolute top-6 left-6 h-10 w-10 rounded-md shadow-md flex items-center justify-center z-10 ${
                  selectedImage.is_favorited ? 'bg-rose-500 text-white hover:bg-rose-600' : 'bg-white hover:bg-gray-100'
                }`}
                title={selectedImage.is_favorited ? 'お気に入りを解除' : 'お気に入りに追加'}
              >
                <Heart className={`w-5 h-5 ${selectedImage.is_favorited ? 'fill-current' : ''}`} />
              </button>
              <CachedImg
                imageId={selectedImage.id}
                src={selectedImage.url}
                alt={selectedImage.prompt}
                className="max-w-full max-h-full object-contain"
              />
              <ImageVersionNavigator
                image={selectedImage}
                onSelect={(version) => setSelectedImage(version)}
              />
            </div>
            <div className="w-full lg:w-80 p-6 overflow-y-auto max-h-[45vh] lg:max-h-none">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">画像の詳細</h3>
                <button
                  onClick={() => setSelectedImage(null)}
                  className="h-8 w-8 p-0 hover:bg-gray-100 rounded-md"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                {/* Prompt - Shown for freetext mode, or all modes in staging/local environment */}
                {(shouldShowPrompt() || selectedImage.input_mode === 'freetext') && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">プロンプト</label>
                    <p className="text-sm text-gray-600 mt-1">{selectedImage.prompt}</p>
                  </div>
                )}

                {selectedImage.description && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">説明</label>
                    <p className="text-sm text-gray-600 mt-1">{selectedImage.description}</p>
                  </div>
                )}

                {selectedImage.product_name && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">商品情報</label>
                    <p className="text-sm text-gray-600 mt-1">{selectedImage.product_name}</p>
                  </div>
                )}

                {selectedImage.style_id && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">スタイル適合</label>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="truncate text-sm text-gray-600">{selectedImage.style_name ?? '選択スタイル'}</span>
                      <StyleFeedbackButtons
                        imageId={selectedImage.id}
                        styleId={selectedImage.style_id}
                        currentRating={selectedImage.style_rating ?? null}
                        size="md"
                        source="image_detail_panel"
                        onRated={(rating) => updateImageStyleRating(selectedImage.id, rating)}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-700">ファイルサイズ</label>
                  <p className="text-sm text-gray-600 mt-1">
                    {(selectedImage.file_size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">生成日時</label>
                  <p className="text-sm text-gray-600 mt-1">
                    {new Date(selectedImage.created_at).toLocaleString('ja-JP')}
                  </p>
                </div>


                {/* Ad Score */}
                {adScoreEnabled && selectedImage.ad_score != null && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">効果予測スコア</label>
                    <p className="text-sm text-gray-600 mt-1">{selectedImage.ad_score}点</p>
                  </div>
                )}


                <div className="space-y-2 pt-4">
                  {/* Use this prompt button - Shown for freetext mode, or all modes in staging/local environment */}
                  {(shouldShowPrompt() || selectedImage.input_mode === 'freetext') && (
                    <button
                      onClick={() => {
                        setPrompt(selectedImage.prompt);
                        setSelectedImage(null);
                        // Scroll to prompt section after modal closes
                        setTimeout(() => {
                          promptSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                      }}
                      className="w-full bg-blue-500 hover:bg-primary text-white py-2 px-4 rounded-md transition-colors"
                    >
                      このプロンプトを使う
                    </button>
                  )}
                  <button
                    onClick={() => handleUseImageAsReference(selectedImage)}
                    className="w-full bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-md transition-colors"
                  >
                    この画像を使う
                  </button>
                  <button
                    onClick={() => setShowImageEditModal(true)}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white py-2 px-4 rounded-md transition-colors"
                  >
                    この画像を修正する
                  </button>
                  <button
                    onClick={() => {
                      setShowMagicResizeModal(true);
                    }}
                    className="w-full bg-purple-500 hover:bg-purple-600 text-white py-2 px-4 rounded-md transition-colors"
                  >
                    マジックリサイズ
                  </button>
                  {selectedImage.s3_key && (
                    <button
                      onClick={() => navigateToVideo(selectedImage, () => setSelectedImage(null))}
                      className="w-full bg-indigo-500 hover:bg-indigo-600 text-white py-2 px-4 rounded-md transition-colors flex items-center justify-center gap-2"
                    >
                      <Video className="w-4 h-4" />
                      動画にする
                    </button>
                  )}
                  <button
                    onClick={() => handleDownloadImage(selectedImage.id, selectedImage.url, selectedImage.prompt)}
                    className="w-full border border-warm-border hover:bg-gray-50 py-2 px-4 rounded-md transition-colors"
                  >
                    ダウンロード
                  </button>
                  <button
                    onClick={() => {
                      handleDeleteImage(selectedImage.id);
                      setSelectedImage(null);
                    }}
                    className="w-full border border-red-300 text-red-600 hover:bg-red-50 py-2 px-4 rounded-md transition-colors"
                  >
                    削除
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Job Detail Modal (for viewing histogram from image detail) */}
      {jobDetailJobId && (
        <JobDetailModal
          jobId={jobDetailJobId}
          isOpen={true}
          onClose={() => setJobDetailJobId(null)}
        />
      )}

      {/* Product Image Selection Modal */}
      <ProductImageSelectionModal
        isOpen={showImageSelectionModal}
        onClose={() => setShowImageSelectionModal(false)}
        onSelect={handleReferenceImagesSelect}
        maxSelection={5}
      />

      {/* Product Info Copy Modal */}
      <ProductInfoCopyModal
        isOpen={showProductInfoModal}
        onClose={() => setShowProductInfoModal(false)}
        onCopyToPrompt={handleCopyToPrompt}
      />

      {showProductSelectionModal && (
        <ProductSelectionModal
          isOpen={showProductSelectionModal}
          onClose={() => setShowProductSelectionModal(false)}
          onConfirm={handleProductSelected}
        />
      )}

      {/* AI Prompt Generator Modal */}
      <PromptGeneratorModal
        isOpen={showPromptGeneratorModal}
        onClose={() => setShowPromptGeneratorModal(false)}
        onConfirm={handleAIPromptConfirm}
        referenceImages={[
          // Convert library reference images
          ...referenceImages.map(img => ({
            url: img.url,
            category: img.category,
          })),
          // Convert uploaded reference images
          ...uploadedReferenceImages.map(img => ({
            url: img.preview,
            category: img.category,
          })),
        ]}
      />

      {/* Banner Library Modal */}
      <BannerLibraryModal
        isOpen={showBannerLibraryModal}
        onClose={() => setShowBannerLibraryModal(false)}
        onSelect={handleBannerSelect}
        maxSelection={5}
      />

      {/* Material Style Select Modal */}
      {showStyleSelectModal && (
        <StyleSelectModal
          isOpen={showStyleSelectModal}
          onClose={() => setShowStyleSelectModal(false)}
          onSelect={(style) => {
            setSelectedStyle(style);
            setSelectedStyles([]);
            setShowStyleSelectModal(false);
          }}
          onSelectMany={(styles) => {
            setSelectedStyles(styles);
            setSelectedStyle(null);
            setShowStyleSelectModal(false);
          }}
          currentStyleId={selectedStyle?.id}
          currentStyleIds={selectedStyles.map(style => style.id)}
          multiSelect={imageCount >= 2}
          maxSelection={imageCount}
          fetchFunctions={{
            getStyles: () => materialStyleService.getStyles(),
            getStyleCategories: () => materialStyleService.getStyleCategories(),
          }}
        />
      )}

      {/* Image Edit Modal */}
      {selectedImage && showImageEditModal && (
        <BannerImageEditModal
          image={selectedImage}
          isOpen={showImageEditModal}
          onClose={() => setShowImageEditModal(false)}
          onApply={(editedImage) => {
            setSelectedImage(editedImage);
            setShowImageEditModal(false);
            setDisplayedContent(prev => [editedImage, ...prev]);
          }}
        />
      )}

      {/* Magic Resize Modal */}
      <MagicResizeModal
        isOpen={showMagicResizeModal}
        onClose={() => setShowMagicResizeModal(false)}
        imageId={selectedImage?.id ?? null}
        imageUrl={selectedImage?.url ?? null}
        generationSource="image_generation"
        onSuccess={() => {
          setJobRefreshTrigger((prev) => prev + 1);
          setShowMagicResizeModal(false);
          setSelectedImage(null);
        }}
      />
    </ResponsiveAppShell>
  );
};

export default ImageGenerationPage;
